import {
  Avatar,
  Box,
  Divider,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Paper,
  Stack,
  Typography,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import React from 'react';
import Button from '../components/Button';
import { MEDICATION_STATUS } from '../constants';
import MedicationIcon from '@mui/icons-material/Medication';
import { isEmpty } from 'lodash';
import moment from 'moment';
import { getWeekdaysWithDates } from '../utils';
import { useTranslation } from 'react-i18next';
import WeekdayDateSelector from './WeekdayDateSelector';
import { getUserMedicationWeeklyStats, getMedicationWeeklyStats } from '../utils/api';
import useStore from '../Store/Store';
import { useQuery } from 'react-query';

const MedicationReminderSidebar = ({ todayMedications, handleUpdateEvent, onFilterDate }) => {
  const [selectedDay, setSelectedDay] = React.useState(3);
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const user = useStore((state) => state.user);
  const selectedRecipient = useStore((state) => state.selectedRecipient);
  const { data: medicationWeeklyStats, refetch: refetchMedicationWeeklyStats } = useQuery({
    queryKey: ['medicationWeeklyStats', user?._id, selectedRecipient || 'user'],
    queryFn: () => {
      if (selectedRecipient) {
        return getMedicationWeeklyStats({ userId: user?._id, recipientId: selectedRecipient });
      } else {
        return getUserMedicationWeeklyStats({ userId: user?._id });
      }
    },
    enabled: !!user?._id,
    refetchOnWindowFocus: false,
    staleTime: 0,
  });
  const medicationWeeklyStatsData = medicationWeeklyStats?.data;

  return (
    <Paper
      sx={{
        width: isMobile ? '100%' : 'auto',
        height: isMobile ? 'auto' : '65vh',
        p: isMobile ? 2 : 3,
        borderRadius: 2,
        bgcolor: 'background.paper',
        mr: isMobile ? 0 : 2,
        mb: isMobile ? 2 : 0,
        boxShadow: 'none',
        overflow: 'scroll',
      }}
      className="hide-scroll"
    >
      <Stack spacing={isMobile ? 2 : 3}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            bgcolor: 'background.paper',
            borderRadius: 1,
            p: isMobile ? 1 : 2,
          }}
        >
          <Typography variant={isMobile ? 'h6' : 'h5'}>Today, {moment().format('MMM DD')}</Typography>
          <Box sx={{ marginTop: '10px', pb: 2 }}>
            <WeekdayDateSelector
              selectedDay={selectedDay}
              onDateSelect={(index, day) => {
                setSelectedDay(index);
                onFilterDate(day.format('YYYY-MM-DD'));
              }}
              weeklyStats={medicationWeeklyStatsData}
              showArrow={true}
              arrowPosition={selectedDay}
            />
          </Box>
        </Box>

        <List sx={{ width: '100%' }}>
          {isEmpty(todayMedications) && <Typography variant="body1">{t('noRemindersFound')}</Typography>}
          {!isEmpty(todayMedications) &&
            todayMedications.map((item, index) => (
              <React.Fragment key={index}>
                <ListItem
                  alignItems="flex-start"
                  sx={{
                    flexDirection: isMobile ? 'column' : 'row',
                    gap: isMobile ? 1 : 0,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', width: '100%' }}>
                    <ListItemAvatar>
                      <Avatar
                        sx={{
                          bgcolor: item?.medicationData?.color || 'green',
                        }}
                      >
                        <MedicationIcon />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Typography
                          variant="body1"
                          sx={{
                            textDecoration: item.status !== MEDICATION_STATUS.pending ? 'line-through' : 'none',
                          }}
                        >
                          {item.title}
                        </Typography>
                      }
                      secondary={
                        <>
                          <Typography variant="body2">{moment.unix(item?.start).format('h:mm a')}</Typography>
                          <Typography variant="body2">{item?.medicationData?.howToTake || ''}</Typography>
                        </>
                      }
                    />
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1,
                      alignItems: isMobile ? 'stretch' : 'center',
                      width: isMobile ? '100%' : 'auto',
                      mt: isMobile ? 1 : 0,
                    }}
                  >
                    {item.status === MEDICATION_STATUS.pending && item.start <= moment().unix() && (
                      <div style={{ display: isMobile ? 'flex' : 'grid', gap: isMobile ? '8px' : 0 }}>
                        <Button
                          text="Take"
                          style={{
                            mb: isMobile ? 0 : 1,
                            fontSize: 12,
                            width: isMobile ? '100%' : 80,
                          }}
                          onClick={async () => {
                            await handleUpdateEvent(item?._id, MEDICATION_STATUS.completed);
                            setTimeout(() => {
                              refetchMedicationWeeklyStats();
                            }, 500);
                          }}
                        />

                        <Button
                          text="Skip"
                          bg="light"
                          style={{
                            fontSize: 12,
                            width: isMobile ? '100%' : 80,
                          }}
                          onClick={async () => {
                            await handleUpdateEvent(item?._id, MEDICATION_STATUS.cancelled);
                            setTimeout(() => {
                              refetchMedicationWeeklyStats();
                            }, 500);
                          }}
                        />
                      </div>
                    )}
                    {item.status === MEDICATION_STATUS.completed && (
                      <Button
                        text={'taken'}
                        bg="light"
                        disabled={true}
                        style={{
                          fontSize: 12,
                          width: isMobile ? '100%' : 80,
                        }}
                      />
                    )}
                    {item.status === MEDICATION_STATUS.cancelled && (
                      <Button
                        text={t('skipped')}
                        bg="light"
                        disabled={true}
                        style={{
                          fontSize: 12,
                          width: isMobile ? '100%' : 80,
                        }}
                      />
                    )}
                  </Box>
                </ListItem>
                {index < 2 && <Divider />}
              </React.Fragment>
            ))}
        </List>
      </Stack>
    </Paper>
  );
};

export default MedicationReminderSidebar;
